import { useEffect, useState } from "react";
import { advisoryApi, type I18n, type Json } from "../advisoryApi";
import { i18n, isRecord, toNumber } from "../format";
import { SaveBar, Seg } from "../ui";
import { useSaver, useStepLoad, type StepProps } from "./common";

interface Bar {
  key: string;
  labelI18n: I18n;
  steps: { key: string; labelI18n: I18n }[];
}
interface SustData {
  options: {
    config: {
      bars?: Bar[];
      sfdr?: { scores: { value: number; labelI18n: I18n }[]; labelI18n: I18n } | null;
      productPreferences?: unknown[];
    };
  };
  current: { inputData: { important?: boolean; preferences?: Json | null } | null } | null;
}

interface Prefs {
  sfdr: string;
  bars: Record<string, string>;
  alignmentMode: "individual" | "portfolio";
  thresholdPct: string;
}

const EMPTY: Prefs = { sfdr: "", bars: {}, alignmentMode: "individual", thresholdPct: "" };

function toPrefs(p: Json | null | undefined): Prefs {
  if (!p) return EMPTY;
  return {
    sfdr: p.sfdr != null ? String(p.sfdr) : "",
    bars: isRecord(p.bars) ? (Object.fromEntries(Object.entries(p.bars).map(([k, v]) => [k, String(v)])) as Record<string, string>) : {},
    alignmentMode: p.alignmentMode === "portfolio" ? "portfolio" : "individual",
    thresholdPct: p.thresholdPct != null ? String(p.thresholdPct) : "",
  };
}

/**
 * Sustainability (`sustainability`) — QP's sustainability widget: the gate
 * "Is sustainability important?", then the SFDR minimum, one choice per
 * configured bar, and how alignment is measured.
 *
 * Found live (§12.3): with any bar preference the matched model portfolio
 * lands in a sustainability deviation, and resolving a deviation is only
 * possible in Quantfolio's own screens. So over Andersen's connection this
 * flow can be submitted with "Not important" only. The advisor is told so
 * plainly, before choosing — it is a known limit, not an error.
 */
export function SustainabilityStep({ sessionId, readOnly, onSaved }: StepProps) {
  const load = useStepLoad<SustData>(sessionId, "sustainability", {});
  const saver = useSaver(onSaved);
  const [important, setImportant] = useState<boolean | null>(null);
  const [prefs, setPrefs] = useState<Prefs>(EMPTY);
  const [base, setBase] = useState("");

  useEffect(() => {
    if (!load.data) return;
    const input = load.data.current?.inputData;
    const imp = typeof input?.important === "boolean" ? input.important : null;
    const p = toPrefs(input?.preferences ?? null);
    setImportant(imp);
    setPrefs(p);
    setBase(JSON.stringify([imp, p]));
  }, [load.data]);

  if (load.loading && !load.data) return <p className="muted">Loading sustainability preferences…</p>;
  if (load.error) return <div className="banner-error">{load.error}</div>;

  const cfg = load.data?.options.config ?? {};
  const bars = cfg.bars ?? [];
  const dirty = JSON.stringify([important, prefs]) !== base;
  const barsComplete = bars.every((b) => prefs.bars[b.key]);

  async function save() {
    if (important === null) return;
    const body: Json = important
      ? {
          important: true,
          preferences: {
            ...(prefs.sfdr ? { sfdr: Number(prefs.sfdr) } : {}),
            bars: prefs.bars,
            alignmentMode: prefs.alignmentMode,
            ...(prefs.alignmentMode === "portfolio" && toNumber(prefs.thresholdPct) !== null
              ? { thresholdPct: toNumber(prefs.thresholdPct) }
              : {}),
          },
        }
      : { important: false };
    const r = await saver.run(() => advisoryApi.saveStep(sessionId, "sustainability", {}, body));
    if (r) setBase(JSON.stringify([important, prefs]));
  }

  return (
    <section className="adv-step-card">
      <div className="field">
        <span>Is sustainability important to the client? *</span>
        <Seg
          value={important === null ? null : important ? "yes" : "no"}
          options={[
            { value: "yes", label: "Yes, important" },
            { value: "no", label: "Not important" },
          ]}
          onChange={(v) => setImportant(v === "yes")}
          disabled={readOnly}
          label="Sustainability important"
        />
      </div>

      <div className="adv-info-note">
        <strong>Good to know</strong>
        <p>
          From Andersen, this advice can be submitted when sustainability is <em>not important</em> to the client. If the client
          does have preferences, the recommended funds don't yet carry the sustainability data to match them, and that mismatch
          can only be signed off in Quantfolio. Record the preferences there, or note the client's view in the advisor note below.
        </p>
      </div>

      {important && (
        <div className="adv-subsection">
          {cfg.sfdr && cfg.sfdr.scores.length > 0 && (
            <label className="field">
              <span>{i18n(cfg.sfdr.labelI18n, "Minimum SFDR category")}</span>
              <select value={prefs.sfdr} onChange={(e) => setPrefs({ ...prefs, sfdr: e.target.value })} disabled={readOnly}>
                <option value="">No minimum</option>
                {cfg.sfdr.scores.map((s) => (
                  <option key={s.value} value={String(s.value)}>
                    {i18n(s.labelI18n)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {bars.map((b) => (
            <div className="field" key={b.key}>
              <span>{i18n(b.labelI18n)} *</span>
              <Seg
                value={prefs.bars[b.key] ?? null}
                options={b.steps.map((s) => ({ value: s.key, label: i18n(s.labelI18n) }))}
                onChange={(v) => setPrefs({ ...prefs, bars: { ...prefs.bars, [b.key]: v } })}
                disabled={readOnly}
                label={i18n(b.labelI18n)}
              />
            </div>
          ))}
          <div className="field">
            <span>How should alignment be measured?</span>
            <Seg
              value={prefs.alignmentMode}
              options={[
                { value: "individual", label: "Each fund" },
                { value: "portfolio", label: "Whole portfolio" },
              ]}
              onChange={(v) => setPrefs({ ...prefs, alignmentMode: v })}
              disabled={readOnly}
              label="Alignment mode"
            />
          </div>
          {prefs.alignmentMode === "portfolio" && (
            <label className="field adv-field-narrow">
              <span>Share of the portfolio that must align (%)</span>
              <input
                type="number"
                min={0}
                max={100}
                value={prefs.thresholdPct}
                onChange={(e) => setPrefs({ ...prefs, thresholdPct: e.target.value })}
                disabled={readOnly}
              />
            </label>
          )}
        </div>
      )}

      <SaveBar
        onSave={save}
        saving={saver.saving}
        dirty={dirty}
        error={saver.error}
        readOnly={readOnly}
        disabled={important === null || (important && !barsComplete)}
      />
    </section>
  );
}
